import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Search,
  Menu,
  X,
  User,
  LogIn,
  Sun,
  Moon,
  Monitor,
  Globe,
  LogOut,
  LayoutDashboard,
  Bookmark,
  Settings,
  History,
  Bell,
  SlidersHorizontal,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/notification/NotificationBell";

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const currentLang = i18n.language || "fr";

  // Track scroll position to add shadow and shrink header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsMoreOpen(false);
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-user-menu]")) {
        setIsUserMenuOpen(false);
      }
      if (!target.closest("[data-more-menu]")) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const navLinks = [
    { to: `/${currentLang}`, label: t("nav.home") },
    { to: `/${currentLang}/catalog`, label: t("nav.catalog") },
    { to: `/${currentLang}/authors`, label: t("nav.authors") },
    {
      to: `/${currentLang}/institutions`,
      label: t("nav.institutions"),
    },
    { to: `/${currentLang}/countries`, label: t("nav.countries") },
    {
      to: `/${currentLang}/classic-authors`,
      label: t("nav.classic_authors"),
    },
    { to: `/${currentLang}/about`, label: t("nav.about") },
    { to: `/${currentLang}/contact`, label: t("nav.contact") },
  ];

  // First 3 always visible on desktop; the rest live in the "More" dropdown
  const primaryLinks = navLinks.slice(0, 3);
  const moreLinks = navLinks.slice(3);

  const isActiveLink = (path: string) => {
    if (path === `/${currentLang}` || path === `/${currentLang}/`) {
      return (
        location.pathname === `/${currentLang}` ||
        location.pathname === `/${currentLang}/`
      );
    }
    return location.pathname.startsWith(path);
  };

  const handleLanguageToggle = () => {
    const newLang = currentLang === "fr" ? "en" : "fr";
    i18n.changeLanguage(newLang);
    localStorage.setItem("katalog-language", newLang);

    // Update URL to reflect new language
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|fr)/, "");
    navigate(`/${newLang}${pathWithoutLang || "/"}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate(`/${currentLang}/login`);
  };

  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        "border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60",
        isScrolled && "shadow-md border-border/40",
        !isScrolled && "border-transparent",
      )}
    >
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        {/* ── Logo ──────────────────────────────────────────────────────── */}
        <Link
          to={`/${currentLang}`}
          className="flex items-center gap-2 transition-transform hover:scale-105"
        >
          <motion.div
            initial={{ rotate: -10 }}
            animate={{ rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 10 }}
          >
            <BookOpen className="h-7 w-7 text-primary" />
          </motion.div>
          <span className="font-heading text-xl font-bold tracking-tight text-foreground">
            Katalog
          </span>
        </Link>

        {/* ── Desktop Navigation ────────────────────────────────────────── */}
        <nav className="hidden items-center gap-1 md:flex" role="navigation">
          {/* Primary links (always visible) */}
          {primaryLinks.map((link) => {
            const active = isActiveLink(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "relative px-3 py-2 text-sm font-medium transition-colors",
                  "rounded-md hover:bg-accent/50 hover:text-accent-foreground",
                  active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {link.label}
                {active && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}

          {/* "More" dropdown */}
          <div className="relative" data-more-menu>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsMoreOpen((prev) => !prev);
              }}
              className={cn(
                "relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors",
                "rounded-md hover:bg-accent/50 hover:text-accent-foreground",
                moreLinks.some((l) => isActiveLink(l.to))
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {t("nav.more")}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  isMoreOpen && "rotate-180",
                )}
              />
              {moreLinks.some((l) => isActiveLink(l.to)) && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </button>

            <AnimatePresence>
              {isMoreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-lg border bg-popover p-1 shadow-lg"
                  data-more-menu
                >
                  {moreLinks.map((link) => {
                    const active = isActiveLink(link.to);
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setIsMoreOpen(false)}
                        className={cn(
                          "block rounded-md px-3 py-2 text-sm font-medium transition-colors",
                          active
                            ? "bg-accent text-accent-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* ── Right Section ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-1.5">
          {/* Search icon button */}
          <button
            type="button"
            onClick={() => {
              // Dispatch Ctrl+K event to open command palette
              const event = new KeyboardEvent("keydown", {
                key: "k",
                ctrlKey: true,
                bubbles: true,
              });
              document.dispatchEvent(event);
            }}
            className={cn(
              "hidden rounded-md p-2 text-muted-foreground transition-colors sm:inline-flex",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            aria-label={t("nav.search")}
            title="Ctrl+K"
          >
            <Search className="h-4 w-4" />
          </button>

          {/* Language switcher */}
          <motion.button
            type="button"
            onClick={handleLanguageToggle}
            className={cn(
              "rounded-md p-2 text-muted-foreground transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            aria-label={t("accessibility.toggle_language")}
            title={
              currentLang === "fr"
                ? t("header.switch_to_english")
                : t("header.switch_to_french")
            }
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              key={currentLang}
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: -90, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-1 text-xs font-semibold"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline uppercase">
                {currentLang === "fr" ? "EN" : "FR"}
              </span>
            </motion.div>
          </motion.button>

          {/* Theme toggle */}
          <motion.button
            type="button"
            onClick={toggleTheme}
            className={cn(
              "rounded-md p-2 text-muted-foreground transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            aria-label={t("accessibility.toggle_theme")}
            title={`${t("accessibility.toggle_theme")}: ${t("common.theme_" + theme)}`}
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              key={theme}
              initial={{ rotate: -90, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
            >
              <ThemeIcon className="h-4 w-4" />
            </motion.div>
          </motion.button>

          {/* Notification bell */}
          {isAuthenticated && <NotificationBell />}

          {/* User menu / Login button */}
          {isAuthenticated && user ? (
            <div className="relative" data-user-menu>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUserMenuOpen((prev) => !prev);
                }}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isUserMenuOpen && "bg-accent",
                )}
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {user.first_name.charAt(0)}
                  {user.last_name.charAt(0)}
                </div>
                <span className="hidden text-sm font-medium md:inline">
                  {user.first_name}
                </span>
              </button>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -5, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className={cn(
                      "absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-lg border",
                      "bg-popover p-1 shadow-lg",
                    )}
                    data-user-menu
                  >
                    <div className="border-b px-3 py-2 text-sm">
                      <p className="font-medium">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>

                    <div className="py-1">
                      <Link
                        to={`/${currentLang}/dashboard`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        {t("nav.dashboard")}
                      </Link>
                      <Link
                        to={`/${currentLang}/bookmarks`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <Bookmark className="h-4 w-4" />
                        {t("nav.bookmarks")}
                      </Link>
                      <Link
                        to={`/${currentLang}/history`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <History className="h-4 w-4" />
                        {t("nav.history")}
                      </Link>
                      <Link
                        to={`/${currentLang}/notifications`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <Bell className="h-4 w-4" />
                        {t("nav.notifications")}
                      </Link>
                      <Link
                        to={`/${currentLang}/profile`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <User className="h-4 w-4" />
                        {t("nav.profile")}
                      </Link>
                      <Link
                        to={`/${currentLang}/profile/preferences`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                        {t("nav.preferences")}
                      </Link>

                      {(user.role === "admin" ||
                        user.role === "super_admin") && (
                        <Link
                          to={`/${currentLang}/admin`}
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent"
                        >
                          <Settings className="h-4 w-4" />
                          {t("nav.admin")}
                        </Link>
                      )}
                    </div>

                    <div className="border-t py-1">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10"
                      >
                        <LogOut className="h-4 w-4" />
                        {t("nav.logout")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link
              to={`/${currentLang}/login`}
              className={cn(
                "hidden items-center gap-2 rounded-md px-3 py-2 text-sm font-medium sm:inline-flex",
                "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
                "active:scale-[0.97]",
              )}
            >
              <LogIn className="h-4 w-4" />
              {t("nav.login")}
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className={cn(
              "rounded-md p-2 text-muted-foreground transition-colors md:hidden",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            aria-label={
              isMobileMenuOpen
                ? t("accessibility.close_menu")
                : t("accessibility.open_menu")
            }
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={isMobileMenuOpen ? "close" : "open"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </motion.div>
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* ── Mobile Navigation ────────────────────────────────────────────── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden border-t md:hidden"
          >
            <nav className="container mx-auto flex flex-col gap-1 px-4 py-3">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.to}
                    className={cn(
                      "block rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isActiveLink(link.to)
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}

              {/* Mobile auth links */}
              <div className="mt-2 border-t pt-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to={`/${currentLang}/dashboard`}
                      className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {t("nav.dashboard")}
                    </Link>
                    <Link
                      to={`/${currentLang}/profile`}
                      className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50"
                    >
                      <User className="h-4 w-4" />
                      {t("nav.profile")}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("nav.logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to={`/${currentLang}/login`}
                      className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/50"
                    >
                      <LogIn className="h-4 w-4" />
                      {t("nav.login")}
                    </Link>
                    <Link
                      to={`/${currentLang}/register`}
                      className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-accent/50"
                    >
                      <User className="h-4 w-4" />
                      {t("nav.register")}
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
